import APIService from './APIService'
import qs from 'qs'

const api = APIService()

const base = (portfolioId) => `/portfolios/${portfolioId}/documents`

export const getPortfolioFiles = ({ portfolioId, query, buildingPassportId }, signal) => {
  let queryValues = { page: 1, limit: 3000 }
  if (query) queryValues = { ...queryValues, ...query }
  if (buildingPassportId) queryValues = { ...queryValues, 'filter[building_passport_id]': buildingPassportId }
  const queryString = qs.stringify(queryValues, { addQueryPrefix: true })
  return api.get(`/portfolios/${portfolioId}/aggregate-files${queryString}`, signal ? { signal } : {})
}

export const getPortfolioFileCounts = ({ portfolioId, query, buildingPassportId }) => {
  let queryValues = query ? { ...query } : {}
  if (buildingPassportId) queryValues = { ...queryValues, 'filter[building_passport_id]': buildingPassportId }
  const queryString = Object.keys(queryValues).length > 0 ? qs.stringify(queryValues, { addQueryPrefix: true }) : ''
  return api.get(`/portfolios/${portfolioId}/aggregate-files/counts${queryString}`)
}

export const searchPortfolioFiles = (payload, signal) => {
  const { portfolioId, query, uploaderIds, fileTypeIds, fileCategoryIds, fileTagIds } = payload
  const params = { query: query || '' }
  if (uploaderIds?.length > 0) params.uploader_ids = uploaderIds
  if (fileTypeIds?.length > 0) params.file_type_ids = fileTypeIds
  if (fileCategoryIds?.length > 0) params.file_category_ids = fileCategoryIds
  if (fileTagIds?.length > 0) params.file_tag_ids = fileTagIds
  const queryString = qs.stringify(params, { arrayFormat: 'brackets', skipNulls: true })
  return api.get(`/portfolios/${portfolioId}/aggregate-files/search?${queryString}`, signal ? { signal } : {})
}

export const getPortfolioFolderTree = (portfolioId) =>
  api.get(`${base(portfolioId)}/folders/tree`)

export const getPortfolioFolderUserCounts = (portfolioId) =>
  api.get(`${base(portfolioId)}/folders/user-counts`)

export const getPortfolioFolderUsers = (portfolioId, folderId) =>
  api.get(`${base(portfolioId)}/folders/${folderId}/users`)

export const getPortfolioFolderFiles = (portfolioId, folderId, page = 1, limit = 30, filters = {}) =>
  api.get(`${base(portfolioId)}/folders/${folderId}/files`, {
    params: {
      page,
      limit,
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.fileTypeIds && filters.fileTypeIds.length ? { fileTypeIds: filters.fileTypeIds } : {}),
    },
  })

export const createPortfolioFolder = (portfolioId, body) =>
  api.post(`${base(portfolioId)}/folders`, body)

export const updatePortfolioFolder = (portfolioId, folderId, body) =>
  api.patch(`${base(portfolioId)}/folders/${folderId}`, body)

export const deletePortfolioFolder = (portfolioId, folderId) =>
  api.delete(`${base(portfolioId)}/folders/${folderId}`)

export const movePortfolioFolder = (portfolioId, folderId, parentFolderId) =>
  api.patch(`${base(portfolioId)}/folders/${folderId}/move`, { parentFolderId })

export const togglePortfolioFolderAccess = (portfolioId, userId, folders) =>
  api.patch(`${base(portfolioId)}/folders/access`, { userId, folders })

export const presignPortfolioDocument = (portfolioId, displayName) =>
  api.post(`${base(portfolioId)}/files/presign`, { displayName })

export const startPortfolioMultiUpload = (portfolioId, totalFiles) =>
  api.post(`${base(portfolioId)}/files/multi-upload`, { totalFiles })

export const presignPortfolioMultiUploadFile = (portfolioId, processId, displayName) =>
  api.post(`${base(portfolioId)}/files/multi-upload/${processId}/presigned-url`, { displayName })

export const addFileToPortfolioMultiUpload = (portfolioId, processId, body) =>
  api.post(`${base(portfolioId)}/files/multi-upload/${processId}/file`, body)

export const getPortfolioMultiUploadProgress = (portfolioId, processId) =>
  api.get(`${base(portfolioId)}/files/multi-upload/${processId}`)

export const uploadPortfolioDocuments = (portfolioId, body) =>
  api.post(`${base(portfolioId)}/files`, body)

export const movePortfolioFile = (portfolioId, fileId, portfolioFolderId) =>
  api.patch(`${base(portfolioId)}/files/${fileId}/move`, { portfolioFolderId })

export const updatePortfolioFile = (portfolioId, fileId, body) =>
  api.patch(`${base(portfolioId)}/files/${fileId}`, body)

export const bulkMovePortfolioFiles = (portfolioId, fileIds, portfolioFolderId) =>
  api.post(`${base(portfolioId)}/files/bulk-assign`, { fileIds, portfolioFolderId })

export const bulkRemovePortfolioFiles = (portfolioId, fileIds) =>
  api.post(`${base(portfolioId)}/files/bulk-remove`, { fileIds })

export const bulkSharePortfolioFilesToBps = (portfolioId, fileIds, buildingPassportIds) =>
  api.post(`${base(portfolioId)}/files/bulk-share-to-bps`, { fileIds, buildingPassportIds })

export const unassignPortfolioFile = (portfolioId, fileId) =>
  api.delete(`${base(portfolioId)}/files/${fileId}`)

export const bulkAssignPortfolioFiles = (portfolioId, fileIds, portfolioFolderId) =>
  api.post(`${base(portfolioId)}/files/bulk-assign`, { fileIds, portfolioFolderId })

export const sharePortfolioFile = (portfolioId, fileId, body) =>
  api.post(`${base(portfolioId)}/files/${fileId}/share`, body)

export const sharePortfolioFileWithBps = (portfolioId, fileId, buildingPassportIds) =>
  api.post(`${base(portfolioId)}/files/${fileId}/share-to-bps`, { buildingPassportIds })

export const listPortfolioFileSharedUsers = (portfolioId, fileId) =>
  api.get(`${base(portfolioId)}/files/${fileId}/shared-users`)

export const sharePortfolioFileWithUser = (portfolioId, fileId, email) =>
  api.post(`${base(portfolioId)}/files/${fileId}/share-user`, { email })

export const unsharePortfolioFileUser = (portfolioId, fileId, userId) =>
  api.delete(`${base(portfolioId)}/files/${fileId}/share-user/${userId}`)

export const revokePortfolioFileExternalShare = (portfolioId, fileId, shareId) =>
  api.delete(`${base(portfolioId)}/files/${fileId}/external-share/${shareId}`)

export const listPortfolioFileBuildingShares = (portfolioId, fileId) =>
  api.get(`${base(portfolioId)}/files/${fileId}/bp-shares`)

export const removePortfolioFileBuildingShare = (portfolioId, fileId, shareId) =>
  api.delete(`${base(portfolioId)}/files/${fileId}/bp-shares/${shareId}`)

export const getPortfolioFileInsight = (portfolioId, fileId) =>
  api.post(`${base(portfolioId)}/files/${fileId}/insight`)
